import React, { useState, useEffect } from 'react';
import './App.css';
import { usePackSizes } from './hooks/usePackSizes';
import { useCalculation } from './hooks/useCalculation';

const PackSizeRow = ({ value, index, onUpdate, onRemove, canRemove }) => {
  const [inputValue, setInputValue] = useState(value);
  const [error, setError] = useState(false);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleBlur = () => {
    const num = parseInt(inputValue, 10);
    if (isNaN(num) || !Number.isInteger(num) || num <= 0) {
      setError(true);
      setInputValue(value);
      return;
    }
    setError(false);
    if (num !== value) {
      onUpdate(index, num);
    }
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    if (raw === "") {
      setInputValue("");
      return;
    }
    const intVal = parseInt(raw, 10);
    if (!isNaN(intVal)) {
      setInputValue(intVal);
    }
    setError(false);
  };

  return (
    <tr>
      <td style={{ width: '70%' }}>
        <input
          type="number"
          value={inputValue}
          onChange={handleChange}
          onBlur={handleBlur}
          step="1"
          min="1"
          className={`pack-input ${error ? 'error' : ''}`}
        />
        {error && <div className="error-text">Positive integer only</div>}
      </td>
      <td style={{ width: '30%', textAlign: 'center' }}>
        <button
          onClick={() => onRemove(index)}
          disabled={!canRemove}
          className="remove-btn"
        >
          Delete
        </button>
      </td>
    </tr>
  );
};


const ResultsTable = ({ results }) => {
  if (!results || results.length === 0) {
    return (
      <div className="empty-state">
        <p style={{ fontSize: '12px', marginTop: '8px' }}>Enter items and click "Calculate"</p>
      </div>
    );
  }

  return (
    <>
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th>Pack Size</th>
              <th>Quantity</th>
            </tr>
          </thead>
          <tbody>
            {results.map((item, idx) => (
              <tr key={idx}>
                <td><strong>{item.pack}</strong></td>
                <td>{item.quantity}x</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

// Main App Component
function App() {
  const {
    packSizes,
    loading,
    updatePackSize,
    addPackSize,
    removePackSize,
  } = usePackSizes();

  const {
    itemsWanted,
    setItemsWanted,
    results,
    calculating,
    error,
    calculate
  } = useCalculation();

  const handleCalculate = () => {
    calculate(packSizes);
  };

  if (loading) {
    return (
      <div className="app">
        <div className="loading">
          <div className="spinner"></div>
          <p style={{ marginTop: '12px' }}>Loading pack sizes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <div className="app-header">
        <h1>Order Pack Calculator</h1>
      </div>

      <div className="layout">
        {/* Left Card - Pack Sizes */}
        <div className="card">
          <div className="card-header">
            <h2>Pack Sizes</h2>
          </div>
          <div className="card-body">
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Pack Size (units)</th>
                    <th style={{ width: '80px' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {packSizes.map((size, idx) => (
                    <PackSizeRow
                      key={idx}
                      value={size}
                      index={idx}
                      onUpdate={updatePackSize}
                      onRemove={removePackSize}
                      canRemove={packSizes.length > 1}
                    />
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="button-group">
              <button onClick={addPackSize} className="btn btn-secondary">
                + Add Pack Size
              </button>
            </div>
          </div>
        </div>
        
        {/* Right Card - Calculator */}
        <div className="card">
          <div className="card-header">
            <h2>Calculator</h2>
          </div>
          <div className="card-body">
            <div className="input-group">
              <label>Items <span style={{ color: '#64748b' }}>(positive integer)</span></label>
              <div className="input-row">
                <input
                  type="number"
                  value={itemsWanted}
                  onChange={(e) => {
                    setItemsWanted(e.target.value);
                  }}
                  min="1"
                  step="1"
                  className="number-input"
                />
                <button 
                  onClick={handleCalculate} 
                  disabled={calculating || !packSizes || packSizes.length === 0}
                  className="btn btn-primary"
                >
                  {calculating ? 'Calculating...' : 'Calculate'}
                </button>
              </div>
              {error && (
                <div className="alert alert-error" style={{ marginTop: '12px' }}>
                  {error}
                </div>
              )}
            </div>
            
            <ResultsTable results={results} />
            
            {packSizes && packSizes.length > 0 && (
              <div className="pack-size-badge">
                <strong>Available pack sizes:</strong>
                <div className="pack-size-list">
                  {[...new Set(packSizes)].sort((a, b) => a - b).map((size, idx) => (
                    <span key={idx} className="pack-size-tag">{size}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
